/**
 * AssertionTracker — Captures and links assertions to API calls
 *
 * Records every assertion made on API response data (both passing and failing),
 * links each to the originating API call by call ID, and flushes the data to
 * testInfo.annotations for the reporter to consume.
 */

import { readFileSync } from 'node:fs';
import { fileURLToPath } from 'node:url';
import type { ApiAssertion, AssertionType, AssertionLocation } from '@testrelic/core';
import { stripAnsi } from './ansi.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Symbol used to tag APIResponse objects with their call ID. */
export const CALL_ID_SYMBOL: unique symbol = Symbol.for('__testrelic_call_id');

/** Annotation type for assertion data transport (fixture → reporter). */
export const ASSERTION_ANNOTATION_TYPE = '__testrelic_api_assertions';

/** WeakMap to track call IDs for object values extracted from responses. */
export const valueCallIdMap = new WeakMap<object, string>();

// ---------------------------------------------------------------------------
// TestInfo-like interface (avoids importing Playwright test internals)
// ---------------------------------------------------------------------------

interface TestInfoLike {
  annotations: Array<{ type: string; description?: string }>;
}

// ---------------------------------------------------------------------------
// AssertionTracker
// ---------------------------------------------------------------------------

export class AssertionTracker {
  private assertions: ApiAssertion[] = [];
  private currentCallId: string | null = null;

  /** Record a captured assertion. */
  recordAssertion(assertion: ApiAssertion): void {
    this.assertions.push(assertion);
  }

  /** Get all recorded assertions. */
  getAssertions(): readonly ApiAssertion[] {
    return this.assertions;
  }

  /** Set the most recent API call ID for temporal fallback linking. */
  setCurrentCallId(callId: string): void {
    this.currentCallId = callId;
  }

  /** Get the most recent API call ID. */
  getCurrentCallId(): string | null {
    return this.currentCallId;
  }

  /** Return assertion data for consolidated payload. */
  getData(): ApiAssertion[] {
    return [...this.assertions];
  }

  /** Flush assertion data to testInfo annotations (legacy). */
  flushLegacyAnnotations(testInfo: TestInfoLike): void {
    if (this.assertions.length === 0) return;

    testInfo.annotations.push({
      type: ASSERTION_ANNOTATION_TYPE,
      description: JSON.stringify(this.assertions),
    });
  }

  /** Clear all state for garbage collection. */
  dispose(): void {
    this.assertions = [];
    this.currentCallId = null;
  }
}

// ---------------------------------------------------------------------------
// Assertion Location Capture
// ---------------------------------------------------------------------------

/** Cache for source file lines to avoid repeated reads. */
const sourceCache = new Map<string, string[]>();

function getSourceLines(filePath: string): string[] | null {
  const cached = sourceCache.get(filePath);
  if (cached) return cached;

  try {
    // V8 stack frames are file:// URLs under ESM (and always on Windows, e.g.
    // `file:///C:/…`). readFileSync needs a filesystem path, so normalize first
    // — otherwise the source line (and thus the assertion expression + type)
    // can't be read and every assertion degrades to a generic "custom" one.
    const path = filePath.startsWith('file://') ? fileURLToPath(filePath) : filePath;
    const content = readFileSync(path, 'utf-8');
    const lines = content.split('\n');
    sourceCache.set(filePath, lines);
    return lines;
  } catch {
    return null;
  }
}

/**
 * Parse the call stack to find the assertion location in user test code.
 * Returns the first stack frame that is NOT inside the SDK's own source files.
 */
/**
 * The directory this module ended up in.
 *
 * The package is bundled per entry point with `splitting: false`, so at
 * runtime this code lives in `fixture.cjs` / `api-fixture.cjs` / etc., never
 * in a file called `assertion-tracker`. Matching on the source filename
 * therefore skipped nothing, and every captured location pointed at a line of
 * our own wrapper instead of the user's test.
 */
const OWN_STACK_DIR: string | null = (() => {
  const stack = new Error().stack;
  if (!stack) return null;
  for (const line of stack.split('\n')) {
    const m = line.match(/at\s+(?:.*?\s+\()?(.+?):\d+:\d+\)?$/);
    if (!m || m[1].startsWith('node:')) continue;
    // Source maps rewrite bundled frames back to their ORIGINAL source
    // paths, so the frame to skip can name any module in this package, not
    // only this one. The directory they share is the reliable marker.
    const cut = Math.max(m[1].lastIndexOf('/'), m[1].lastIndexOf('\\'));
    return cut > 0 ? m[1].slice(0, cut + 1) : null;
  }
  return null;
})();

/** A stack-frame path as a plain filesystem path. */
function toPlainPath(file: string): string {
  if (!file.startsWith('file:')) return file;
  try { return fileURLToPath(file); } catch { return file; }
}

function captureAssertionLocation(): AssertionLocation | null {
  const err = new Error();
  // SAFETY: Error.stack is V8-specific but guaranteed on Node.js 18+
  const stack = err.stack;
  if (!stack) return null;

  const lines = stack.split('\n');

  // Two passes. The first skips our own code; the second skips only the
  // machinery that is ours by name, so a stack the first pass rejects
  // entirely still yields a real file and line rather than `unknown:0`.
  return findFrame(lines, true) ?? findFrame(lines, false);
}

/**
 * First frame that is not ours.
 *
 * `excludeOwnDir` is what separates the two passes. Note what is deliberately
 * NOT here: a filter on our own source filenames. `'/assertion-tracker.'`
 * used to be in this list, and it matches the path of any file whose name
 * merely STARTS that way — including this module's own test,
 * `assertion-tracker.test.ts`. On Windows the separator is a backslash so the
 * substring never matched and the filter looked harmless; on POSIX it dropped
 * the very frame being looked for, every frame was skipped, and the location
 * came back as the `unknown` fallback. `OWN_STACK_DIR` identifies our code by
 * the directory it actually runs from, which is both correct and not
 * defeatable by what a user names a test file.
 */
function findFrame(lines: readonly string[], excludeOwnDir: boolean): AssertionLocation | null {
  for (const line of lines) {
    if (
      (excludeOwnDir && OWN_STACK_DIR !== null && line.includes(OWN_STACK_DIR)) ||
      line.includes('node_modules') ||
      line.includes('node:internal') ||
      line.includes(' at new Error') ||
      line.includes(' at captureAssertionLocation') ||
      line.includes(' at findFrame') ||
      line.includes(' at expectWrapper') ||
      line.includes(' at wrappedMatcher')
    ) {
      continue;
    }

    // Match V8 stack frame: "    at functionName (file:line:column)"
    // or "    at file:line:column"
    const match = line.match(/at\s+(?:.*?\s+\()?(.+?):(\d+):(\d+)\)?$/);
    if (match) {
      return {
        // ESM frames are file:// URLs and CJS frames are plain paths. Callers
        // compare this against paths from Playwright, so normalise here.
        file: toPlainPath(match[1]),
        line: parseInt(match[2], 10),
        column: parseInt(match[3], 10),
      };
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Assertion Type Detection
// ---------------------------------------------------------------------------

/**
 * Infer the assertion type from the source expression and the value being asserted.
 */
function inferAssertionType(
  expression: string | undefined,
  // SAFETY: value is the raw asserted value from user code — may be any type
  _value: unknown,
): AssertionType {
  if (!expression) return 'custom';

  const expr = expression.toLowerCase();

  if (expr.includes('.status()') && !expr.includes('.statustext()')) {
    return expr.includes('.ok()') ? 'statusOk' : 'status';
  }
  if (expr.includes('.ok()')) return 'statusOk';
  if (expr.includes('.headers()') || expr.includes('.headersarray()')) return 'header';
  if (expr.includes('tomatchobject') || expr.includes('toequal')) return 'bodyMatch';
  if (expr.includes('tocontain') || expr.includes('tostringcontaining')) return 'bodyContains';

  // If expression references json() or text() it's a body field
  if (expr.includes('.json()') || expr.includes('.text()')) return 'bodyField';

  // If none of the above match, default to bodyField for parsed data, or custom
  return 'custom';
}

// ---------------------------------------------------------------------------
// Assertion-Aware Expect Wrapper
// ---------------------------------------------------------------------------

/** Minimal interface for ApiRequestTracker to avoid circular imports. */
interface ApiTrackerLike {
  readonly lastCallId: string | null;
  getCallIdForValue(value: unknown): string | null;
}

/**
 * Creates a wrapped `expect` that captures assertion metadata and records it
 * via the AssertionTracker. Preserves all original Playwright expect behavior.
 */
function buildExpectWrapper(
  getTracker: () => AssertionTracker | null,
  getApiTracker: () => ApiTrackerLike | null,
  // SAFETY: originalExpect is the Playwright expect — typed as Function to avoid
  // importing @playwright/test in this module's runtime
  originalExpect: (...args: unknown[]) => unknown,
): (...args: unknown[]) => unknown {
  const wrappedExpect = function expectWrapper(received: unknown, ...rest: unknown[]): unknown {
    // Resolve the assertion context (explicit per-instance, or the active
    // per-test context set by the fixture). With no context, this is a
    // transparent pass-through to the raw Playwright expect.
    const tracker = getTracker();
    const apiTracker = getApiTracker();
    if (!tracker || !apiTracker) {
      return (originalExpect as Function)(received, ...rest);
    }

    // Determine call ID from the value being asserted
    let callId: string | null = null;

    // Strategy 1: Check if the value itself is tagged (response object)
    if (received !== null && received !== undefined && typeof received === 'object') {
      const tagged = (received as Record<symbol, string>)[CALL_ID_SYMBOL];
      if (typeof tagged === 'string') {
        callId = tagged;
      }
    }

    // Strategy 2: Check if value is in the WeakMap (extracted from response)
    if (!callId && received !== null && received !== undefined && typeof received === 'object') {
      callId = valueCallIdMap.get(received as object) ?? null;
    }

    // Strategy 3: Check via the API tracker's value-to-callId mapping
    if (!callId) {
      callId = apiTracker.getCallIdForValue(received);
    }

    // Strategy 4: Temporal fallback — use the most recent API call
    if (!callId) {
      callId = tracker.getCurrentCallId();
    }

    // If no call ID found, this assertion is not related to an API call — skip capture
    if (!callId) {
      return (originalExpect as Function)(received, ...rest);
    }

    // Capture location before the assertion runs
    const location = captureAssertionLocation();

    // Read the source expression (best-effort)
    let expression: string | undefined;
    if (location) {
      const lines = getSourceLines(location.file);
      if (lines && location.line > 0 && location.line <= lines.length) {
        expression = lines[location.line - 1].trim();
      }
    }

    // Get the matchers object from the original expect
    const matchers = (originalExpect as Function)(received, ...rest);

    // If no callId at this point, just return the matchers directly
    if (!callId) return matchers;

    // Wrap the matchers to intercept assertion calls
    return createMatcherProxy(matchers as object, {
      tracker,
      callId,
      received,
      location: location ?? { file: 'unknown', line: 0 },
      expression,
    });
  };

  // Copy static methods from originalExpect (e.g., expect.soft, expect.configure, etc.)
  //
  // SAFETY: both sides are functions, which have no index signature, so
  // reading and writing arbitrary statics needs a widening step. The keys come
  // from `Object.keys` of the original, never from user input.
  const statics = originalExpect as unknown as Record<string, unknown>;
  const target = wrappedExpect as unknown as Record<string, unknown>;
  for (const key of Object.keys(statics)) {
    target[key] = statics[key];
  }

  return wrappedExpect;
}

/**
 * Per-instance assertion-aware expect — records into the explicitly-provided
 * tracker. Used by unit tests and any caller holding tracker references.
 */
export function createAssertionAwareExpect(
  tracker: AssertionTracker,
  apiTracker: ApiTrackerLike,
  originalExpect: (...args: unknown[]) => unknown,
): (...args: unknown[]) => unknown {
  return buildExpectWrapper(() => tracker, () => apiTracker, originalExpect);
}

// ---------------------------------------------------------------------------
// Active assertion context (fixture → exported `expect`)
//
// Fixtures export a single module-level `expect`, but the trackers are created
// per-test inside the fixture. Playwright runs tests serially within a worker,
// so the fixture sets an "active context" at test start and clears it at test
// end; the exported `expect` records into whichever test is currently running.
// ---------------------------------------------------------------------------

/**
 * The active context lives on `globalThis`, not in a module variable.
 *
 * The package ships several entry points (`/fixture`, `/api-fixture`,
 * `/visual`) built with `splitting: false`, so each bundle carries its OWN
 * copy of this module — and its own module-level variables. The documented
 * setup imports `test` from one entry and `expect` from another, which meant
 * the fixture set the context in one copy while `expect` read `null` from
 * another and silently passed every assertion straight through. A single
 * process-wide slot is what makes the two halves meet.
 */
interface AssertionContextSlot {
  tracker: AssertionTracker | null;
  apiTracker: ApiTrackerLike | null;
}

const ACTIVE_CONTEXT_KEY = Symbol.for('testrelic.activeAssertionContext');

function contextSlot(): AssertionContextSlot {
  const g = globalThis as unknown as Record<symbol, AssertionContextSlot | undefined>;
  let slot = g[ACTIVE_CONTEXT_KEY];
  if (!slot) {
    slot = { tracker: null, apiTracker: null };
    g[ACTIVE_CONTEXT_KEY] = slot;
  }
  return slot;
}

/** Set (or clear, with nulls) the active per-test assertion context. */
export function setActiveAssertionContext(
  tracker: AssertionTracker | null,
  apiTracker: ApiTrackerLike | null,
): void {
  const slot = contextSlot();
  slot.tracker = tracker;
  slot.apiTracker = apiTracker;
}

/**
 * Assertion-aware expect that records into whichever tracker is currently
 * active (see setActiveAssertionContext). Falls back to raw expect when none —
 * so it is safe to construct once at module load and import everywhere.
 */
export function createActiveAssertionAwareExpect(
  originalExpect: (...args: unknown[]) => unknown,
): (...args: unknown[]) => unknown {
  return buildExpectWrapper(
    () => contextSlot().tracker,
    () => contextSlot().apiTracker,
    originalExpect,
  );
}

// ---------------------------------------------------------------------------
// Matcher Proxy
// ---------------------------------------------------------------------------

interface MatcherContext {
  tracker: AssertionTracker;
  callId: string;
  received: unknown;
  location: AssertionLocation;
  expression: string | undefined;
}

/**
 * Creates a Proxy around the matchers object returned by expect().
 * Intercepts matcher method calls (toBe, toEqual, etc.) to record assertions.
 */
function createMatcherProxy(matchers: object, ctx: MatcherContext): object {
  return new Proxy(matchers, {
    get(target: object, prop: string | symbol, receiver: unknown): unknown {
      const value = Reflect.get(target, prop, receiver);

      // Only intercept function calls (matcher methods)
      if (typeof value !== 'function') return value;

      // Skip internal Playwright properties
      if (typeof prop === 'symbol') return value;

      // Handle .not — return a new proxy with negation
      if (prop === 'not') {
        const negated = value;
        return createMatcherProxy(
          negated as object,
          ctx,
        );
      }

      // Wrap the matcher function to capture the assertion
      return function wrappedMatcher(this: unknown, ...args: unknown[]): unknown {
        const expected = args[0];
        let status: 'passed' | 'failed' = 'passed';
        let actual: unknown = ctx.received;
        let thrownError: unknown = null;

        try {
          const result = (value as Function).apply(this ?? target, args);

          // If result is a Promise (async matchers), handle it
          if (result && typeof result === 'object' && typeof (result as { then?: unknown }).then === 'function') {
            return (result as Promise<unknown>).then(
              () => {
                recordAssertionResult(ctx, prop, expected, actual, 'passed');
              },
              (err: unknown) => {
                recordAssertionResult(ctx, prop, expected, actual, 'failed');
                throw err;
              },
            );
          }

          return result;
        } catch (err: unknown) {
          status = 'failed';
          thrownError = err;

          // Try to extract actual value from the error message
          if (err instanceof Error && err.message) {
            const receivedMatch = err.message.match(/Received:\s*(.+)/);
            if (receivedMatch) {
              actual = receivedMatch[1];
            }
          }

          throw err;
        } finally {
          // Only record sync assertions here; async assertions are handled above
          if (thrownError !== null || status === 'passed') {
            recordAssertionResult(ctx, prop, expected, actual, status);
          }
        }
      };
    },
  });
}

function recordAssertionResult(
  ctx: MatcherContext,
  matcherName: string,
  expected: unknown,
  actual: unknown,
  status: 'passed' | 'failed',
): void {
  const assertionType = inferAssertionType(ctx.expression, ctx.received);

  const assertion: ApiAssertion = {
    callId: ctx.callId,
    type: assertionType,
    expected: safeSerializable(expected),
    actual: safeSerializable(actual),
    status,
    location: ctx.location,
    expression: ctx.expression,
  };

  ctx.tracker.recordAssertion(assertion);
}

/**
 * Ensure a value is safely serializable to JSON.
 * Converts non-serializable values to string representations.
 */
function safeSerializable(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  // Values scraped out of a matcher's error message carry Playwright's ANSI
  // colour codes. They are invisible in a terminal and garbage everywhere
  // else — the HTML report, the JSON file and the cloud payload all showed
  // "\u001b[31m100\u001b[39m" where the received value should be.
  if (typeof value === 'string') return stripAnsi(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  try {
    JSON.stringify(value);
    return value;
  } catch {
    return String(value);
  }
}
